<template>
  <p :class="[{ simple: isSimple, tdk2: !isSimple }]">
    <span
      :class="[
        { spacebar: spacebarNow && isSimple, focusable: isCurrentCharacter(i) },
        'noselect',
        `char${i + 1}`,
        getClasses(i, letter)
      ]"
      v-for="(letter, i) in splitText"
      :key="letter + i"
      v-html="letter"
      :role="isCurrentCharacter(i) ? 'region' : null"
      :aria-live="isCurrentCharacter(i) ? 'assertive' : null"
      :tabindex="isCurrentCharacter(i) ? 0 : -1"
    ></span>
  </p>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { characterMap } from 'constants/standard-keyboard';

const activity = createNamespacedHelpers('activity');
const typingActivity = createNamespacedHelpers('activity/typing');

export default {
  props: {
    text: {
      type: String,
      default: ''
    },
    answer: {
      type: String,
      require: false
    },
    textStep: {
      type: Number,
      required: true
    },
    hintClass: {
      type: String,
      required: false
    },
    isSimple: {
      type: Boolean,
      required: false
    }
  },

  data() {
    return {
      cursorColorInit: '', // Boolean
      typeSizeInit: '',
      letterSpacingInit: '',
      txtInit: '',
      canSelect: true,
      enabled: false,
      colorsDark: ['F1A12E', 'FDDE28', 'AED067', '8DB6D1', '958DB9'],
      errorColor: 'cc0000',
      cursorPos: '',
      noSelection: true,
      selector: '',
      cursorSelector: '',
      spacebarNow: false,
      characterMap
    };
  },

  computed: {
    ...activity.mapState(['progress']),
    ...typingActivity.mapState({
      typingProgress: 'progress',
      attemptedKey: 'attemptedKey'
    }),

    isCurrentStep() {
      return this.progress.stepIndex === this.textStep;
    },

    isCompletedStep() {
      return this.progress.stepIndex > this.textStep;
    },

    isWrongKey() {
      return this.attemptedKey.character && !this.attemptedKey.isCorrect;
    },

    hasBeenCorrectlyTyped() {
      return index => this.isCurrentStep && this.typingProgress.currentIndex > index;
    },

    splitText() {
      return (this.text || '').replace(' ', '\xa0').split('');
    }
  },

  methods: {
    getHintAdditionalClass() {
      return this.hintClass ? this.hintClass : '';
    },

    getRowClass(letter) {
      return characterMap.get(letter.toLowerCase());
    },

    isCurrentCharacter(index) {
      let result = this.isCurrentStep && this.typingProgress.currentIndex === index;
      if (this.answer) {
        result = result && this.typingProgress.currentIndex < this.answer.length;
      }
      return result;
    },

    getClasses(index, letter) {
      const trueLetter = letter === '\xa0' ? ' ' : letter;
      const isCurrentCharacter = this.isCurrentCharacter(index);
      const classes = {
        expected: isCurrentCharacter,
        charDone: this.hasBeenCorrectlyTyped(index) || this.isCompletedStep,
        error: this.isSimple ? false : isCurrentCharacter && this.isWrongKey,
        spacebar: this.isSimple ? false : trueLetter === ' ',
        [this.getRowClass(trueLetter)]: true,
        hint: this.isSimple ? false : isCurrentCharacter && this.getHintAdditionalClass()
      };
      return classes;
    }
  }
};
</script>

<style lang="scss" scoped>
.noselect {
  -webkit-touch-callout: none;
  user-select: none;
  cursor: default;
}

p {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-family: hwtprint;
}

// BASE PLUS CURSORHEIGHT
p.tdk2 {
  color: black;
}

p.tdk2 span {
  border: 1px solid white;
}

.spacebar {
  min-width: 25px;
}

p.tdk2 span.charDone {
  color: black;
}

p .hint {
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: -100px;
    left: 40px;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background-color: #629bc0;
  }
}

// Expected key highlighted in a color-box
.expected {
  margin-right: 10px;
  background-repeat: no-repeat;
  background-color: white;
  color: black;
  background-size: contain;
  background-position: center center;
  // padding: remValues(15, 50);
  padding: 15px 50px;

  // Number Row Keys
  &.row1 {
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz48c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMTI4IDEyOCIgZW5hYmxlLWJhY2tncm91bmQ9Im5ldyAwIDAgMTI4IDEyOCIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+PHBhdGggZmlsbD0iI0ZBRDRBNCIgZD0iTTEyMC4zLDI3LjV2NzMuMWMwLDguOC03LjEsMTUuOS0xNS45LDE1LjlIMjMuNmMtOC44LDAtMTUuOS03LjEtMTUuOS0xNS45VjI3LjVjMC04LjgsNy4xLTE1LjksMTUuOS0xNS45aDgwLjhDMTEzLjIsMTEuNiwxMjAuMywxOC42LDEyMC4zLDI3LjV6Ii8+PHBhdGggZmlsbD0iIzAwMDAwMCIgZD0iTTEyMS45LDEwMWMwLjItNC4xLDAuNy03LjYsMS4yLTEwLjVjMi43LTEyLjcsMy41LTI2LjUsMi4zLTQxLjNjLTAuOS0xMS42LTMuMS0yNC41LTYuNi0zOC44QzEwNy41LDYuOSw5MC4yLDUuMyw2Nyw1LjVjLTI2LjEsMS00NC4zLDEuNy01NC40LDJjLTAuNCwwLjUtMS44LDEuOC00LjEsMy44Yy0xLjQsMS4zLTIuNSwyLjktMy4yLDQuOWMtMi4xLDMuMi0zLjIsOS0zLjMsMTcuNGMwLjMsOS44LDAuNCwxNi42LDAuNCwyMC41QzIuMSw3Mi45LDIsODMuNSwyLjIsODUuOGMxLDE1LjUsNC41LDI1LjMsMTAuNCwyOS41YzQuOCwzLjYsMTMuMiw1LjYsMjUsNmM2LjcsMC4yLDE2LjgsMC4xLDMwLjMtMC4yYzIuOCwwLDgtMC4yLDE1LjYtMC42YzYuNy0wLjEsMTEuMiwwLjUsMTMuNSwyYzcsMCwxMS45LTEsMTQuNi0yLjljNS43LTEuOCw4LjktNS4zLDkuNy0xMC40QzEyMS41LDEwOC4zLDEyMS43LDEwNS41LDEyMS45LDEwMSBNODMuNSwxMTMuMWMtNy42LDAuNC0xMi44LDAuNi0xNS42LDAuNmMtMTEuNiwwLjUtMjAuMywwLjgtMjYuMSwwLjljLTEwLjEsMC4xLTE4LjQtMS4yLTI0LjgtMy45Yy01LjMtOS04LjQtMjQuMy05LjQtNDYuMWMtMS0yMS43LDAuNy0zNy45LDUtNDguNmM4LjUtMy43LDE4LjQtNS41LDI5LjgtNS4yYzYuNSwwLjIsMTYuMSwwLjgsMjguOSwyYzMsMC4xLDE2LjUsMC4yLDQwLjMsMC4zbDEuNSw0LjRjMy4yLDQuMyw0LjgsMTYuNSw0LjksMzYuM2MwLjEsMTcuNC0wLjUsMjkuNS0yLDM2LjVjLTAuMywxLjUtMC40LDQuNC0wLjMsOC43YzAuMSwzLjUtMC4zLDYtMS4yLDcuNGMtMC44LDIuOC0xLjksNC42LTMuMSw1LjVjLTEuMywwLjktMy43LDEuNS03LjEsMS44Yy0wLjksMC41LTEuOSwwLjktMywxLjFjLTAuNiwwLjEtMiwwLjMtNC4zLDAuNEM5NC42LDExMy44LDkwLjEsMTEzLjEsODMuNSwxMTMuMXoiLz48L3N2Zz4=');
  }

  // Q Row Keys
  &.row2 {
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KCjxzdmcgdmVyc2lvbj0iMS4xIiBpZD0iTGF5ZXJfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiCgkgd2lkdGg9IjEyOHB4IiBoZWlnaHQ9IjEyOHB4IiB2aWV3Qm94PSIwIDAgMTI4IDEyOCIgZW5hYmxlLWJhY2tncm91bmQ9Im5ldyAwIDAgMTI4IDEyOCIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+CjxwYXRoIGZpbGw9IiNGRkY2Q0MiIGQ9Ik0xMDQuNCwxMTYuNEgyMy42Yy04LjgsMC0xNS45LTcuMS0xNS45LTE1LjlWMjcuNWMwLTguOCw3LjEtMTUuOSwxNS45LTE1LjloODAuOGM4LjgsMCwxNS45LDcuMSwxNS45LDE1LjkKCXY3My4xQzEyMC4zLDEwOS4zLDExMy4yLDExNi40LDEwNC40LDExNi40eiIvPgo8cGF0aCBmaWxsPSIjMDAwMDAwIiBkPSJNMTIxLjksMjdjLTAuMi00LjUtMC40LTcuMy0wLjYtOC4yYy0wLjgtNS4xLTQtOC42LTkuNy0xMC40QzEwOC45LDYuNSwxMDQsNS41LDk3LDUuNQoJYy0yLjMsMS41LTYuOCwyLjEtMTMuNSwyYy03LjYtMC40LTEyLjgtMC42LTE1LjYtMC42QzU0LjQsNi42LDQ0LjMsNi41LDM3LjYsNi43Yy0xMS44LDAuNC0yMC4yLDIuNC0yNSw2CgljLTUuOSw0LjItOS40LDE0LTEwLjQsMjkuNUMyLDQ0LjUsMi4xLDU1LjEsMi40LDczLjljMCwzLjktMC4xLDEwLjctMC40LDIwLjVjMC4xLDguNCwxLjIsMTQuMiwzLjMsMTcuNGMwLjcsMiwxLjgsMy42LDMuMiw0LjkKCWMyLjMsMiwzLjcsMy4zLDQuMSwzLjhjMTAuMSwwLjMsMjguMywxLDU0LjQsMmMyMy4yLDAuMiw0MC41LTEuNCw1MS44LTQuOWMzLjUtMTQuMyw1LjctMjcuMiw2LjYtMzguOGMxLjItMTQuOCwwLjQtMjguNi0yLjMtNDEuMwoJQzEyMi42LDM0LjYsMTIyLjEsMzEuMSwxMjEuOSwyNyBNOTcsMTIuOGMyLjMsMC4xLDMuNywwLjMsNC4zLDAuNGMxLjEsMC4yLDIuMSwwLjYsMywxLjFjMy40LDAuMyw1LjgsMC45LDcuMSwxLjgKCWMxLjIsMC45LDIuMywyLjcsMy4xLDUuNWMwLjksMS40LDEuMywzLjksMS4yLDcuNGMtMC4xLDQuMywwLDcuMiwwLjMsOC43YzEuNSw3LDIuMSwxOS4xLDIsMzYuNWMtMC4xLDE5LjgtMS43LDMyLTQuOSwzNi4zCglsLTEuNSw0LjRjLTIzLjgsMC4xLTM3LjMsMC4yLTQwLjMsMC4zYy0xMi44LDEuMi0yMi40LDEuOC0yOC45LDJjLTExLjQsMC4zLTIxLjMtMS41LTI5LjgtNS4yYy00LjMtMTAuNy02LTI2LjktNS00OC42CgljMS0yMS44LDQuMS0zNy4xLDkuNC00Ni4xYzYuNC0yLjcsMTQuNy00LDI0LjgtMy45YzUuOCwwLjEsMTQuNSwwLjQsMjYuMSwwLjljMi44LDAsOCwwLjIsMTUuNiwwLjZDOTAuMSwxNC45LDk0LjYsMTQuMiw5NywxMi44eiIKCS8+Cjwvc3ZnPg==');
  }

  // A Row Keys
  &.row3 {
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KCjxzdmcgdmVyc2lvbj0iMS4xIiBpZD0iTGF5ZXJfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiCgkgd2lkdGg9IjEyOHB4IiBoZWlnaHQ9IjEyOHB4IiB2aWV3Qm94PSIwIDAgMTI4IDEyOCIgZW5hYmxlLWJhY2tncm91bmQ9Im5ldyAwIDAgMTI4IDEyOCIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+CjxwYXRoIGZpbGw9IiNERkVCQkUiIGQ9Ik0yMy42LDExLjZoODAuOGM4LjgsMCwxNS45LDcuMSwxNS45LDE1Ljl2NzMuMWMwLDguOC03LjEsMTUuOS0xNS45LDE1LjlIMjMuNmMtOC44LDAtMTUuOS03LjEtMTUuOS0xNS45CglWMjcuNUM3LjcsMTguNiwxNC44LDExLjYsMjMuNiwxMS42eiIvPgo8cGF0aCBmaWxsPSIjMDAwMDAwIiBkPSJNNi4xLDEwMWMwLjIsNC41LDAuNCw3LjMsMC42LDguMmMwLjgsNS4xLDQsOC42LDkuNywxMC40YzIuNywxLjksNy42LDIuOSwxNC42LDIuOWMyLjMtMS41LDYuOC0yLjEsMTMuNS0yCgljNy42LDAuNCwxMi44LDAuNiwxNS42LDAuNmMxMy41LDAuMywyMy42LDAuNCwzMC4zLDAuMmMxMS44LTAuNCwyMC4yLTIuNCwyNS02YzUuOS00LjIsOS40LTE0LDEwLjQtMjkuNWMwLjItMi4zLDAuMS0xMi45LTAuMi0zMS43CgljMC0zLjksMC4xLTEwLjcsMC40LTIwLjVjLTAuMS04LjQtMS4yLTE0LjItMy4zLTE3LjRjLTAuNy0yLTEuOC0zLjYtMy4yLTQuOWMtMi4zLTItMy43LTMuMy00LjEtMy44Yy0xMC4xLTAuMy0yOC4zLTEtNTQuNC0yCglDMzcuOCw1LjMsMjAuNSw2LjksOS4yLDEwLjRDNS43LDI0LjcsMy41LDM3LjYsMi42LDQ5LjJDMS40LDY0LDIuMiw3Ny44LDQuOSw5MC41QzUuNCw5My40LDUuOSw5Ni45LDYuMSwxMDEgTTMxLDExNS4yCgljLTIuMy0wLjEtMy43LTAuMy00LjMtMC40Yy0xLjEtMC4yLTIuMS0wLjYtMy0xLjFjLTMuNC0wLjMtNS44LTAuOS03LjEtMS44Yy0xLjItMC45LTIuMy0yLjctMy4xLTUuNWMtMC45LTEuNC0xLjMtMy45LTEuMi03LjQKCWMwLjEtNC4zLDAtNy4yLTAuMy04LjdjLTEuNS03LTIuMS0xOS4xLTItMzYuNWMwLjEtMTkuOCwxLjctMzIsNC45LTM2LjNsMS41LTQuNGMyMy44LTAuMSwzNy4zLTAuMiw0MC4zLTAuMwoJYzEyLjgtMS4yLDIyLjQtMS44LDI4LjktMmMxMS40LTAuMywyMS4zLDEuNSwyOS44LDUuMmM0LjMsMTAuNyw2LDI2LjksNSw0OC42Yy0xLDIxLjgtNC4xLDM3LjEtOS40LDQ2LjFjLTYuNCwyLjctMTQuNyw0LTI0LjgsMy45CgljLTUuOC0wLjEtMTQuNS0wLjQtMjYuMS0wLjljLTIuOCwwLTgtMC4yLTE1LjYtMC42QzM3LjksMTEzLjEsMzMuNCwxMTMuOCwzMSwxMTUuMnoiLz4KPC9zdmc+');
  }

  // Z Row Keys
  &.row4 {
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KCjxzdmcgdmVyc2lvbj0iMS4xIiBpZD0iTGF5ZXJfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiCgkgd2lkdGg9IjEyOHB4IiBoZWlnaHQ9IjEyOHB4IiB2aWV3Qm94PSIwIDAgMTI4IDEyOCIgZW5hYmxlLWJhY2tncm91bmQ9Im5ldyAwIDAgMTI4IDEyOCIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+CjxwYXRoIGZpbGw9IiNEMkU2RUYiIGQ9Ik0xMjAuMywyNy41djczLjFjMCw4LjgtNy4xLDE1LjktMTUuOSwxNS45SDIzLjZjLTguOCwwLTE1LjktNy4xLTE1LjktMTUuOVYyNy41YzAtOC44LDcuMS0xNS45LDE1LjktMTUuOQoJaDgwLjhDMTEzLjIsMTEuNiwxMjAuMywxOC42LDEyMC4zLDI3LjV6Ii8+CjxwYXRoIGZpbGw9IiMwMDAwMDAiIGQ9Ik0xMjEuOSwxMDFjMC4yLTQuMSwwLjctNy42LDEuMi0xMC41YzIuNy0xMi43LDMuNS0yNi41LDIuMy00MS4zYy0wLjktMTEuNi0zLjEtMjQuNS02LjYtMzguOAoJQzEwNy41LDYuOSw5MC4yLDUuMyw2Nyw1LjVjLTI2LjEsMS00NC4zLDEuNy01NC40LDJjLTAuNCwwLjUtMS44LDEuOC00LjEsMy44Yy0xLjQsMS4zLTIuNSwyLjktMy4yLDQuOWMtMi4xLDMuMi0zLjIsOS0zLjMsMTcuNAoJYzAuMyw5LjgsMC40LDE2LjYsMC40LDIwLjVDMi4xLDcyLjksMiw4My41LDIuMiw4NS44YzEsMTUuNSw0LjUsMjUuMywxMC40LDI5LjVjNC44LDMuNiwxMy4yLDUuNiwyNSw2YzYuNywwLjIsMTYuOCwwLjEsMzAuMy0wLjIKCWMyLjgsMCw4LTAuMiwxNS42LTAuNmM2LjctMC4xLDExLjIsMC41LDEzLjUsMmM3LDAsMTEuOS0xLDE0LjYtMi45YzUuNy0xLjgsOC45LTUuMyw5LjctMTAuNEMxMjEuNSwxMDguMywxMjEuNywxMDUuNSwxMjEuOSwxMDEKCSBNODMuNSwxMTMuMWMtNy42LDAuNC0xMi44LDAuNi0xNS42LDAuNmMtMTEuNiwwLjUtMjAuMywwLjgtMjYuMSwwLjljLTEwLjEsMC4xLTE4LjQtMS4yLTI0LjgtMy45Yy01LjMtOS04LjQtMjQuMy05LjQtNDYuMQoJYy0xLTIxLjcsMC43LTM3LjksNS00OC42YzguNS0zLjcsMTguNC01LjUsMjkuOC01LjJjNi41LDAuMiwxNi4xLDAuOCwyOC45LDJjMywwLjEsMTYuNSwwLjIsNDAuMywwLjNsMS41LDQuNAoJYzMuMiw0LjMsNC44LDE2LjUsNC45LDM2LjNjMC4xLDE3LjQtMC41LDI5LjUtMiwzNi41Yy0wLjMsMS41LTAuNCw0LjQtMC4zLDguN2MwLjEsMy41LTAuMyw2LTEuMiw3LjRjLTAuOCwyLjgtMS45LDQuNi0zLjEsNS41CgljLTEuMywwLjktMy43LDEuNS03LjEsMS44Yy0wLjksMC41LTEuOSwwLjktMywxLjFjLTAuNiwwLjEtMiwwLjMtNC4zLDAuNEM5NC42LDExMy44LDkwLjEsMTEzLjEsODMuNSwxMTMuMXoiLz4KPC9zdmc+');
  }

  // Bottom Row Keys (Spacebar)
  &.row5 {
    &,
    &.error {
      background-image: url('data:image/svg+xml;base64,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');
    }

    &.spacebar {
      // padding: remValues(45, 70);
      padding: 40px 70px;
      // margin: remValues(0, 5);
      margin: 0 5px;
    }
  }

  &.error {
    background-image: url('data:image/svg+xml;\
charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%\
0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.2.1%2C%20SVG%20Export%20Plu\
g-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%\
3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20\
xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220p\
x%22%0A%09%20width%3D%22128px%22%20height%3D%22128px%22%20viewBox%3D%220%200%201\
28%20128%22%20enable-background%3D%22new%200%200%20128%20128%22%20xml%3Aspace%3D\
%22preserve%22%3E%0A%3Cpath%20fill%3D%22%23CB2026%22%20d%3D%22M125.1%2C35c-0.5-3\
.2-1-7-1.2-11.5c-0.2-4.9-0.4-8-0.6-9c-0.8-5.6-4.1-9.4-10-11.4C110.4%2C1.1%2C105.\
3%2C0%2C98.1%2C0%0A%09c-2.4%2C1.6-7%2C2.3-13.9%2C2.2c-7.8-0.4-13.2-0.7-16.1-0.7C\
54.1%2C1.2%2C43.7%2C1.1%2C36.8%2C1.3C24.6%2C1.8%2C15.9%2C3.9%2C10.9%2C7.9%0A%09C\
4.9%2C12.5%2C1.2%2C23.2%2C0.2%2C40.1C0%2C42.7%2C0.1%2C54.3%2C0.4%2C74.8c0%2C4.3-\
0.1%2C11.7-0.4%2C22.4c0.1%2C9.2%2C1.2%2C15.5%2C3.4%2C19c0.7%2C2.2%2C1.9%2C3.9%2C\
3.3%2C5.4%0A%09c2.4%2C2.2%2C3.8%2C3.6%2C4.2%2C4.2c10.4%2C0.3%2C29.2%2C1.1%2C56.2\
%2C2.2c24%2C0.2%2C41.8-1.5%2C53.5-5.4c3.6-15.6%2C5.9-29.8%2C6.8-42.4%0A%09C128.7\
%2C64%2C127.9%2C48.9%2C125.1%2C35z%22/%3E%0A%3C/svg%3E%0A');
  }
}

// STYLES FOR isSimple
.simple {
  margin-top: 0;
  margin-bottom: 0;
  display: inline-block;
  text-align: center;
  font-family: 'hwtprint';
  position: relative;
  top: 50%;
  transform: translateY(-50%);

  > span {
    color: #757575;
    border: 1px solid white;
    border-radius: 5px;
    display: inline-block;
    text-align: center;
    padding: 2px 0 2px 2px;

    &.charDone {
      color: black;
    }
  }

  > .expected {
    border: 1px solid black;
    border-radius: 4px;
    color: black;
    padding: 2px 2px 4px;

    &.row1 {
      background-color: #fad4a4;
      background-image: none;
    }

    &.row2 {
      background-color: #fff6cb;
      background-image: none;
    }

    &.row3 {
      background-color: #dfebbe;
      background-image: none;
    }

    &.row4 {
      background-color: #d2e6ee;
      background-image: none;
    }
  }
}
</style>
